/**
 * Audit Trail Helper for CapLedger
 * Provides easy logging of user actions with company context
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';

export interface AuditEventDetails {
  [key: string]: any;
}

export interface AuditEventParams {
  action: string;
  entityType?: string;
  entityId?: string;
  details?: AuditEventDetails;
}

/**
 * Log an audit event to the database
 * Automatically captures user ID and company ID from context
 */
export async function logAuditEvent(
  params: AuditEventParams,
  userId?: string,
  companyId?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current user if not provided
    if (!userId) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        logger.warn('Cannot log audit event: no authenticated user');
        return { success: false, error: 'No authenticated user' };
      }
      userId = user.id;
    }

    // Company ID must be provided
    if (!companyId) {
      logger.warn('Cannot log audit event: no company ID provided', { action: params.action });
      return { success: false, error: 'No company ID provided' };
    }

    // Call the RPC function to log the audit event
    const { data, error } = await supabase.rpc('log_audit_event', {
      p_user_id: userId,
      p_company_id: companyId,
      p_action: params.action,
      p_entity_type: params.entityType || null,
      p_entity_id: params.entityId || null,
      p_details: params.details || null,
    });

    if (error) {
      logger.error('Failed to log audit event', error, {
        action: params.action,
        entityType: params.entityType,
        companyId,
      });
      return { success: false, error: error.message };
    }

    // Mirror audit event to Betterstack for centralized monitoring
    logger.audit(`Audit event: ${params.action}`, {
      auditId: data,
      userId,
      companyId,
      action: params.action,
      entityType: params.entityType,
      entityId: params.entityId,
      details: params.details,
    });

    return { success: true };
  } catch (error) {
    logger.error('Exception while logging audit event', error, {
      action: params.action,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Create a scoped audit logger for a specific company
 * Useful when performing multiple operations within the same company context
 */
export function createCompanyAuditLogger(companyId: string, userId?: string) {
  return {
    log: (params: AuditEventParams) => logAuditEvent(params, userId, companyId),

    // Convenience methods for common audit actions
    companyCreated: (details?: AuditEventDetails) =>
      logAuditEvent(
        { action: 'COMPANY_CREATED', entityType: 'company', details },
        userId,
        companyId
      ),

    shareIssued: (shareholderId: string, details?: AuditEventDetails) =>
      logAuditEvent(
        {
          action: 'SHARE_ISSUED',
          entityType: 'shareholder',
          entityId: shareholderId,
          details,
        },
        userId,
        companyId
      ),

    shareTransferred: (shareholderId: string, details?: AuditEventDetails) =>
      logAuditEvent(
        {
          action: 'SHARE_TRANSFERRED',
          entityType: 'shareholder',
          entityId: shareholderId,
          details,
        },
        userId,
        companyId
      ),

    valuationUpdated: (valuationId: string, details?: AuditEventDetails) =>
      logAuditEvent(
        {
          action: 'VALUATION_UPDATED',
          entityType: 'valuation',
          entityId: valuationId,
          details,
        },
        userId,
        companyId
      ),

    userInvited: (email: string, role: string, details?: AuditEventDetails) =>
      logAuditEvent(
        {
          action: 'USER_INVITED',
          entityType: 'user_role',
          details: { email, role, ...details },
        },
        userId,
        companyId
      ),

    documentUploaded: (fileId: string, fileName: string, details?: AuditEventDetails) =>
      logAuditEvent(
        {
          action: 'DOCUMENT_UPLOADED',
          entityType: 'data_room_file',
          entityId: fileId,
          details: { fileName, ...details },
        },
        userId,
        companyId
      ),

    roundCreated: (roundId: string, details?: AuditEventDetails) =>
      logAuditEvent(
        {
          action: 'FUNDING_ROUND_CREATED',
          entityType: 'financing_round',
          entityId: roundId,
          details,
        },
        userId,
        companyId
      ),

    roundClosed: (roundId: string, details?: AuditEventDetails) =>
      logAuditEvent(
        {
          action: 'FUNDING_ROUND_CLOSED',
          entityType: 'financing_round',
          entityId: roundId,
          details,
        },
        userId,
        companyId
      ),
  };
}

/**
 * Common audit action types (for reference and type safety)
 */
export const AUDIT_ACTIONS = {
  // Company actions
  COMPANY_CREATED: 'COMPANY_CREATED',
  COMPANY_UPDATED: 'COMPANY_UPDATED',
  COMPANY_SETTINGS_CHANGED: 'COMPANY_SETTINGS_CHANGED',

  // Share actions
  SHARE_ISSUED: 'SHARE_ISSUED',
  SHARE_TRANSFERRED: 'SHARE_TRANSFERRED',
  SHARE_CANCELLED: 'SHARE_CANCELLED',

  // Valuation actions
  VALUATION_UPDATED: 'VALUATION_UPDATED',
  VALUATION_CREATED: 'VALUATION_CREATED',

  // User management
  USER_INVITED: 'USER_INVITED',
  USER_ROLE_CHANGED: 'USER_ROLE_CHANGED',
  USER_REMOVED: 'USER_REMOVED',

  // Document actions
  DOCUMENT_UPLOADED: 'DOCUMENT_UPLOADED',
  DOCUMENT_DELETED: 'DOCUMENT_DELETED',
  DOCUMENT_SHARED: 'DOCUMENT_SHARED',

  // Funding round actions
  FUNDING_ROUND_CREATED: 'FUNDING_ROUND_CREATED',
  FUNDING_ROUND_UPDATED: 'FUNDING_ROUND_UPDATED',
  FUNDING_ROUND_CLOSED: 'FUNDING_ROUND_CLOSED',

  // Compliance actions
  FILING_SUBMITTED: 'FILING_SUBMITTED',
  COMPLIANCE_CHECK_RUN: 'COMPLIANCE_CHECK_RUN',

  // Secondary market
  LISTING_CREATED: 'LISTING_CREATED',
  OFFER_PLACED: 'OFFER_PLACED',
  OFFER_ACCEPTED: 'OFFER_ACCEPTED',
} as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[keyof typeof AUDIT_ACTIONS];
